import { useEffect, useState } from "react"
import { useHub } from "../../core/Global"
import { API_ROUTES } from "../../core/Constants"
import Icon from "../base/Icon"
import Indicator from "../base/Indicator"
import { appStateWords, DeviceWord, locationWords, notificationWords, platformName, reportedAt, tapToPayWords } from "../tech/TechDeviceWords"
import { TechBatteryBadge } from "../tech/TechBatteryLabel"

interface DeviceState {
    platform?: string
    appVersion?: string
    osVersion?: string
    deviceModel?: string
    locationStatus?: string
    locationPrecise?: boolean
    notificationsStatus?: string
    tapToPayStatus?: string
    tapToPayDetail?: string
    reportedAt?: string
    batteryLevel?: number
    charging?: boolean
    batteryAt?: string
    appState?: string
    appStateAt?: string
}

type Props = {
    userId?: string
}

/**
 * What the technician's phone allows the app to do, as the app last reported it — on their
 * overview, where the office looks first when something about them is not working: no location
 * on the map, no notification of a new job, no card reader on an invoice.
 *
 * The app reports every time it opens or comes back to the front, so this is never older than
 * the last time they used it.
 */
export default function AccountTechDeviceCard(props: Props) {

    const hub = useHub()

    const [state, stateSet] = useState<DeviceState | null>()

    useEffect(() => {
        if (!props.userId) return

        hub.Get(`${API_ROUTES.Technicians}devicestate/${props.userId}`, res => {
            stateSet(res.success ? (res.data ?? null) : null)
        })
    }, [props.userId])

    const at = reportedAt(state?.reportedAt)
    const appStateAt = reportedAt(state?.appStateAt)
    const phone = [platformName(state?.platform), state?.deviceModel, state?.osVersion].filter(Boolean).join(" · ")

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">
                    <span className="card-label fw-bolder text-dark">Phone & Permissions</span>
                </h3>
                {/* Their phone's battery, opposite the title, as in the drawer and on the map. */}
                <div className="align-self-center">
                    <TechBatteryBadge techId={props.userId} large />
                </div>
            </div>
            <div className="card-body p-9">
                {
                    state === undefined
                        ? <div className="text-center py-5"><Indicator /></div>
                        : state === null
                            ? <div className="fs-6 text-gray-600">
                                Their phone has not reported yet — it does the next time they open the Rinvox app.
                            </div>
                            : <>
                                <div className="row g-5">
                                    <Item icon="location-crosshairs" title="Location"
                                        word={state.locationStatus ? locationWords[state.locationStatus] : undefined}
                                        extra={state.locationPrecise === false && state.locationStatus !== "denied" ? "Approximate location only." : undefined} />
                                    <Item icon="bell" title="Notifications"
                                        word={state.notificationsStatus ? notificationWords[state.notificationsStatus] : undefined} />
                                    <Item icon="credit-card" title="Tap to Pay"
                                        word={state.tapToPayStatus ? tapToPayWords[state.tapToPayStatus] : undefined}
                                        extra={state.tapToPayStatus === "failed" ? state.tapToPayDetail : undefined} />
                                    <Item icon="mobile-screen" title="App"
                                        word={state.appState ? appStateWords[state.appState] : undefined}
                                        extra={appStateAt ? `Since ${appStateAt.toLocaleString()}` : undefined} />
                                </div>

                                {
                                    (phone || state.appVersion) &&
                                    <div className="fs-7 text-gray-600 mt-8 pt-5 border-top">
                                        <Icon icon="mobile-screen" className="me-2 text-gray-500" size={12} />
                                        {[phone, state.appVersion ? `Rinvox ${state.appVersion}` : undefined, at ? `reported ${at.toLocaleString()}` : undefined].filter(Boolean).join(" · ")}
                                    </div>
                                }
                            </>
                }
            </div>
        </div>
    )
}

/** One answer beside its icon — no title, the icon says what it is about (the name stays on
 * the tile as a tooltip). */
function Item(props: { icon: any, title: string, word?: DeviceWord, extra?: string }) {
    return (
        <div className="col col-12 col-md-6 col-xl-3" title={props.title}>
            <div className="d-flex align-items-start">
                <div className={`d-flex align-items-center justify-content-center rounded me-4 bg-light-${props.word?.tone === "gray-600" || !props.word ? "secondary" : props.word.tone}`}
                    style={{ width: 40, height: 40, flex: "0 0 auto" }}>
                    <Icon icon={props.icon} className={`text-${props.word?.tone ?? "gray-500"}`} size={16} />
                </div>
                <div style={{ minWidth: 0 }}>
                    <div className={`fs-5 fw-bold text-${props.word?.tone ?? "gray-500"}`}>{props.word?.label ?? "Not reported"}</div>
                    {props.word && <div className="fs-8 text-gray-600">{props.word.note}</div>}
                    {props.extra && <div className="fs-8 text-gray-600 mt-1">{props.extra}</div>}
                </div>
            </div>
        </div>
    )
}
